import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ContactPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    projectDescription: '',
    instagramHandle: '',
    instagramFollowers: '',
    instagramViews: '',
    facebookPage: '',
    facebookFollowers: '',
    facebookReach: '',
    youtubeChannel: '',
    youtubeSubscribers: '',
    youtubeViews: '',
    tiktokUsername: '',
    tiktokFollowers: '',
    tiktokViews: '',
    otherPlatforms: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Parametri corretti per EmailJS - usando nomi standard
      const templateParams = {
        to_name: 'Mirko Pampanini',
        to_email: 'mirkopampanini01@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        reply_to: formData.email,
        subject: `Nuova Richiesta di Contatto - ${formData.company}`,
        message: `
INFORMAZIONI BASE:
Nome: ${formData.name}
Azienda: ${formData.company}
Email: ${formData.email}
Telefono: ${formData.phone || 'Non fornito'}
Sito Web: ${formData.website || 'Non fornito'}

DESCRIZIONE PROGETTO:
${formData.projectDescription}

SOCIAL MEDIA METRICS:

Instagram:
- Handle: ${formData.instagramHandle || 'Non fornito'}
- Followers: ${formData.instagramFollowers || 'Non fornito'}
- Views: ${formData.instagramViews || 'Non fornito'}

Facebook:
- Pagina: ${formData.facebookPage || 'Non fornito'}
- Followers: ${formData.facebookFollowers || 'Non fornito'}
- Reach: ${formData.facebookReach || 'Non fornito'}

YouTube:
- Canale: ${formData.youtubeChannel || 'Non fornito'}
- Subscribers: ${formData.youtubeSubscribers || 'Non fornito'}
- Views: ${formData.youtubeViews || 'Non fornito'}

TikTok:
- Username: ${formData.tiktokUsername || 'Non fornito'}
- Followers: ${formData.tiktokFollowers || 'Non fornito'}
- Views: ${formData.tiktokViews || 'Non fornito'}

Altre Piattaforme:
${formData.otherPlatforms || 'Nessuna'}
        `
      };

      console.log('Invio parametri EmailJS:', templateParams);

      // Invia email tramite EmailJS
      const result = await window.emailjs.send(
        'service_j8xw3n6',
        'template_cr9gto4',
        templateParams,
        'a6cSoTlkZTx0cNOLg'
      );

      console.log('EmailJS Success:', result);
      setSubmitMessage('Messaggio inviato con successo! Ti contatteremo presto.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        website: '',
        projectDescription: '',
        instagramHandle: '',
        instagramFollowers: '',
        instagramViews: '',
        facebookPage: '',
        facebookFollowers: '',
        facebookReach: '',
        youtubeChannel: '',
        youtubeSubscribers: '',
        youtubeViews: '',
        tiktokUsername: '',
        tiktokFollowers: '',
        tiktokViews: '',
        otherPlatforms: ''
      });

    } catch (error) {
      console.error('Errore EmailJS:', error);
      setSubmitMessage('Errore nell\'invio del messaggio. Riprova più tardi o contattaci direttamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Get Started Today
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Ready to scale your business from 1X to 100X? Fill out the form below and get your 
            personalized digital growth plan within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Submit Message */}
          {submitMessage && (
            <div className={`mb-6 p-4 rounded-lg text-center ${
              submitMessage.includes('successo') 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {submitMessage}
            </div>
          )}

          <form 
            id="contact-form-mirko"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                    Website URL (Optional)
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>

            {/* Project Description */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Details</h2>
              <div>
                <label htmlFor="projectDescription" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Description / Message *
                </label>
                <textarea
                  id="projectDescription"
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Describe your project goals, challenges, and what you're looking to achieve with our digital scaling services..."
                />
              </div>
            </div>

            {/* Social Media Metrics */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Social Profiles & Metrics</h2>
              <p className="text-gray-600 mb-6">Help us understand your current social media presence to create a better strategy.</p>
              
              {/* Instagram */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-instagram-line text-pink-600 mr-2"></i>
                  Instagram
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="instagramHandle" className="block text-sm font-medium text-gray-700 mb-2">
                      Handle
                    </label>
                    <input
                      type="text"
                      id="instagramHandle"
                      name="instagramHandle"
                      value={formData.instagramHandle}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label htmlFor="instagramFollowers" className="block text-sm font-medium text-gray-700 mb-2">
                      Approx. Followers
                    </label>
                    <input
                      type="text"
                      id="instagramFollowers"
                      name="instagramFollowers"
                      value={formData.instagramFollowers}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 10K, 50K, 100K"
                    />
                  </div>
                  <div>
                    <label htmlFor="instagramViews" className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. Views per Post/Story
                    </label>
                    <input
                      type="text"
                      id="instagramViews"
                      name="instagramViews"
                      value={formData.instagramViews}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 5K, 20K"
                    />
                  </div>
                </div>
              </div>

              {/* Facebook */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-facebook-line text-blue-600 mr-2"></i>
                  Facebook
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="facebookPage" className="block text-sm font-medium text-gray-700 mb-2">
                      Page Link
                    </label>
                    <input
                      type="url"
                      id="facebookPage"
                      name="facebookPage"
                      value={formData.facebookPage}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>
                  <div>
                    <label htmlFor="facebookFollowers" className="block text-sm font-medium text-gray-700 mb-2">
                      Approx. Followers
                    </label>
                    <input
                      type="text"
                      id="facebookFollowers"
                      name="facebookFollowers"
                      value={formData.facebookFollowers}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 5K, 25K"
                    />
                  </div>
                  <div>
                    <label htmlFor="facebookReach" className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. Reach
                    </label>
                    <input
                      type="text"
                      id="facebookReach"
                      name="facebookReach"
                      value={formData.facebookReach}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 2K, 10K"
                    />
                  </div>
                </div>
              </div>

              {/* YouTube */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-youtube-line text-red-600 mr-2"></i>
                  YouTube
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="youtubeChannel" className="block text-sm font-medium text-gray-700 mb-2">
                      Channel Link
                    </label>
                    <input
                      type="url"
                      id="youtubeChannel"
                      name="youtubeChannel"
                      value={formData.youtubeChannel}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="https://youtube.com/c/yourchannel"
                    />
                  </div>
                  <div>
                    <label htmlFor="youtubeSubscribers" className="block text-sm font-medium text-gray-700 mb-2">
                      Approx. Subscribers
                    </label>
                    <input
                      type="text"
                      id="youtubeSubscribers"
                      name="youtubeSubscribers"
                      value={formData.youtubeSubscribers}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 1K, 10K, 100K"
                    />
                  </div>
                  <div>
                    <label htmlFor="youtubeViews" className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. Views per Video
                    </label>
                    <input
                      type="text"
                      id="youtubeViews"
                      name="youtubeViews"
                      value={formData.youtubeViews}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 500, 5K, 50K"
                    />
                  </div>
                </div>
              </div>

              {/* TikTok */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-tiktok-line text-black mr-2"></i>
                  TikTok
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="tiktokUsername" className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      id="tiktokUsername"
                      name="tiktokUsername"
                      value={formData.tiktokUsername}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label htmlFor="tiktokFollowers" className="block text-sm font-medium text-gray-700 mb-2">
                      Approx. Followers
                    </label>
                    <input
                      type="text"
                      id="tiktokFollowers"
                      name="tiktokFollowers"
                      value={formData.tiktokFollowers}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 5K, 50K, 500K"
                    />
                  </div>
                  <div>
                    <label htmlFor="tiktokViews" className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. Views per Video
                    </label>
                    <input
                      type="text"
                      id="tiktokViews"
                      name="tiktokViews"
                      value={formData.tiktokViews}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., 1K, 10K, 100K"
                    />
                  </div>
                </div>
              </div>

              {/* Other Platforms */}
              <div>
                <label htmlFor="otherPlatforms" className="block text-sm font-semibold text-gray-700 mb-2">
                  Other Platforms (Optional)
                </label>
                <textarea
                  id="otherPlatforms"
                  name="otherPlatforms"
                  value={formData.otherPlatforms}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="LinkedIn, Twitter, Pinterest, etc. Include handles and approximate metrics..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 cursor-pointer'
                } text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-xl whitespace-nowrap`}
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta di Contatto'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Get in Touch</h2>
            <p className="text-xl text-gray-600">Multiple ways to connect with our digital scaling experts</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Phone</h3>
              <p className="text-gray-600 mb-2">+1 (555) 123-4567</p>
              <p className="text-gray-500 text-sm">Available 24/7 for urgent matters</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Email</h3>
              <p className="text-gray-600 mb-2">hello@digitalscaling.com</p>
              <p className="text-gray-500 text-sm">Response within 2 hours</p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Office</h3>
              <p className="text-gray-600 mb-2">123 Business Street</p>
              <p className="text-gray-600 mb-2">Suite 100, New York, NY 10001</p>
              <p className="text-gray-500 text-sm">Mon-Fri 9AM-6PM EST</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
